import type {ContaVm} from "@/modules/extrato";
import type {BoletoCobrancaLancamentoVm} from "@/modules/boleto";
import * as zod from "zod";

/**
 * Modelo antigo de TB_LANCAMENTO — mantido só pelo que ainda é consumido fora do módulo
 * (extrato/conciliação, transferência, espécie de lançamento do pedido de compra). A tela de
 * Lançamentos em si passou a usar o modelo novo (`LancamentoVm` mais abaixo, com o restante das
 * telas: listagem/cadastro/detalhe).
 */
export interface LancamentoConciliacaoVm {
    id: number;
    conta: ContaVm;
    documento: string;
    numeroDocumento: number;
    especieCobranca: number;
    especieCobrancaDescricao: string;
    especieBaixa: number;
    especieBaixaDescricao: string;
    tipoLancamento: number;
    tipoLancamentoDescricao: string;
    statusLancamento: number;
    statusLancamentoDescricao: string;
    dataLancamento?: Date | null;
    dataLancamentoFormatada?: string | null;
    dataBaixa?: Date | null;
    dataBaixaFormatada?: string | null;
    valorLancamento: number;
    valorBaixa: number;
    favorecido: string;

}

export function dataAsLancamentoConciliacaoVm(data: any): LancamentoConciliacaoVm {
    return data as LancamentoConciliacaoVm;
}

/**
 * ⚠️ Nome mantido com o sufixo antigo por colisão real: este é o modelo de `TB_LANCAMENTO`
 * (`LancamentoVm` no backend, `cgr-prisma-backend.Domain/LancamentoCtx/ViewModels/LancamentoVm.cs`),
 * usado tanto pelo legado (`dataAsLancamentoVm` em `extrato`/`transferencia`, só pra tipar `.conta`
 * no template) quanto pelo modelo REAL da tela de Lançamentos Financeiro V2 — é o shape devolvido por
 * `lancamento/listar` (array), `lancamento/consultar` e `lancamento/detalhe/consultar.lancamento`
 * (confirmado lendo o backend em 2026-09-15, contrato deixou de ser hipotético). Não renomeei pra
 * evitar quebrar extrato/transferencia/pedidoCompra (fora do escopo pedido). O modelo de APRESENTAÇÃO
 * da tela de Lançamentos é `LancamentoTituloVm`, logo abaixo — é derivado DESTE via
 * `lancamentoVmParaTitulo()` (definida logo após `LancamentoTituloVm`), não é o que a API devolve.
 */
export interface LancamentoVm {
    id: number;
    conta: ContaVm;
    contaBaixa: ContaVm;
    documento: string;
    numeroDocumento: number;
    especieCobranca: number;
    especieCobrancaDescricao: string;
    especieBaixa: number;
    especieBaixaDescricao: string;
    tipoLancamento: number;
    tipoLancamentoDescricao: string;
    statusLancamento: number;
    statusLancamentoDescricao: string;
    dataLancamento: Date;
    dataLancamentoFormatada?: string | null;
    dataFaturamento: Date;
    dataFaturamentoFormatada?: string | null;
    dataDocumentoEmissao?: Date | null;
    dataDocumentoEmissaoFormatada?: string | null;
    dataBaixa?: Date | null;
    dataBaixaFormatada?: string | null;
    valorLancamento: number;
    valorBaixa: number;
    favorecido: string;
    centroCusto?: number;
    centroCustoDescricao?: string;
    clienteId?: number;
    fornecedorId?: number;
    funcionarioId?: string;
    transportadoraId?: number;
    // Renomeado de `Observacao` pra `Descricao` no backend (2026-09-19) — é o texto livre digitado no
    // cadastro/alteração, ou herdado de `LancamentoRecorrenciaVm.descricaoLancamento` quando gerado por
    // uma regra. Fonte de verdade pra exibir "descrição" em qualquer tela.
    descricao?: string | null;
    // Reaproveitado (mesmo nome, semântica nova, 2026-09-19): NÃO é mais editável/exibível como
    // descrição do lançamento. Só registra motivo de ações pontuais (cancelamento, prorrogação,
    // abatimento) — só deveria aparecer em histórico/apontamentos, nunca como campo principal.
    observacao?: string;
    statusValorExato: number;
    tipoDocumento: number;
    tipoDocumentoDescricao: string;
    // Confirmado real no backend em 2026-09-19 (`LancamentoVm.LancamentoRecorrenciaId`) — não-nulo
    // quando o lançamento foi gerado por uma regra de recorrência. `TipoDocumento` sozinho não permite
    // essa distinção (lançamento manual e gerado por recorrência usam o mesmo código `SemDocumento`).
    lancamentoRecorrenciaId?: number | null;
    // A PARTIR DAQUI: campos novos da fase de Lançamento Financeiro V2 (baixas/anexos/situação
    // financeira) — derivados em memória no backend, nunca persistidos. Opcionais aqui pra não exigir
    // dos consumidores legados (extrato/transferencia) que nunca vão preencher isso na mão.
    situacaoFinanceira?: number;
    situacaoFinanceiraDescricao?: string;
    saldoAberto?: number;
    valorBaixadoAcumulado?: number;
    // Confirmado real no backend em 2026-09-16 (`LancamentoVm.Parcela`, C#). A partir de 2026-09-21
    // reflete a posição real da ocorrência na série gerada pela regra de recorrência (ex.: "1/3",
    // "2/3", "3/3", ordenado pela data mais antiga = 1); "1/1" continua valendo pra lançamento avulso.
    // Cálculo é sempre do backend — front trata como texto livre, nunca assume/formata "1/1".
    parcela?: string;
    // Natureza financeira (= finalidade da requisição de compra, ver lancamento.commands.ts) — campo
    // novo (2026-09-19), ainda não existe no backend (`LancamentoCtx`). Fica `undefined` até o backend
    // passar a devolvê-lo; usado só pra pré-selecionar o combo em modo edição.
    naturezaFinanceiraId?: number;
    // Boleto atual do lançamento (ativo ou o mais recente) — situação bancária, separada da financeira.
    // Null quando nunca teve boleto. Vem de `lancamento/listar`, `consultar` e `detalhe/consultar`
    // (integração Boleto x Lançamento, 2026-09-24).
    cobranca?: BoletoCobrancaLancamentoVm | null;
}

export function dataAsLancamentoVm(data: any): LancamentoVm {
    return data as LancamentoVm;
}

/** Espécie de cobrança/baixa (dinheiro, cheque, boleto, depósito...) — conceito independente do
 * status/modelo do título, usado por `SelectEspecieLancamento` (consumido também por pedidoCompra). */
export const zodEspecieLancamentoVm = zod.object({
    codigo: zod.number(),
    descricao: zod.string()
}, {message: "Selecione uma espécie"}).refine((val) => val != null);

/**
 * A PARTIR DAQUI: modelo de APRESENTAÇÃO da tela de Lançamentos Financeiro V2. Não é o que a API
 * devolve — é derivado de `LancamentoVm` (acima) via `lancamentoVmParaTitulo()`/
 * `lancamentoDetalheApiParaVm()`, logo abaixo. Mantido com esse shape (em vez de espelhar
 * `LancamentoVm` 1:1 nos componentes) porque `LancamentoLista.vue`/`LancamentoDetalhe.vue` já foram
 * construídos em cima dele — trocar aqui exigiria reescrever os templates, fora do pedido desta
 * sessão (2026-09-15: só a camada de serviço). Campos sem equivalente real no backend (origem)
 * ficam com melhor esforço/`null` — ver comentários nos conversores abaixo. `parcela`
 * passou a ser campo real do backend em 2026-09-16.
 */
/** Mesmos códigos do combo de favorecido do cadastro (`tipoFavorecido` em `LancamentoCadastro.vue`). */
export enum ETipoFavorecidoLancamento {
    Cliente = 1,
    Fornecedor = 2,
    Transportadora = 3,
    Funcionario = 4,
}

/** `ETipoEspecieCobranca.Boleto` no backend. */
export const ESPECIE_COBRANCA_BOLETO = 3;

export enum ETipoLancamento {
    APagar = 1,
    AReceber = 2,
}

export type TSituacaoFinanceiraLancamento = "Previsto" | "EmAberto" | "Parcial" | "Baixado" | "Cancelado";

export type TOrigemLancamento =
    "NFEntrada" | "NFSaida" | "NFServico" | "CTe" | "PedidoCompra" | "Recorrencia" | "Avulso";

/** ⚠️ Nome mantido com "Titulo" por colisão real com `LancamentoVm` (modelo legado, acima) — ver
 * comentário lá. É o Vm principal da tela nova (listagem/cadastro/detalhe). */
export interface LancamentoTituloVm {
    id: number;
    tipo: ETipoLancamento;
    tipoDescricao: string;
    situacaoFinanceira: TSituacaoFinanceiraLancamento;
    situacaoFinanceiraDescricao: string;
    favorecido: string;
    // Espelha o "código + papel" do protótipo (ex.: "CL-0301" · "Cliente") — extensão hipotética ao
    // contrato original, opcional para não quebrar quem já consome só `favorecido`.
    codigoFavorecido?: string;
    papelFavorecido?: string;
    documento: string;
    // "Unidade de operação é a parcela" (PDF §3) — campo real (`LancamentoVm.Parcela`, ver acima),
    // texto livre vindo do backend (posição real na série gerada por recorrência, ou "1/1" pra
    // lançamento avulso — ver comentário em `LancamentoVm.parcela` acima).
    parcela?: string;
    origem: TOrigemLancamento;
    origemDescricao: string;
    // Código bruto (`ETipoDocumentoLancamento`), mantido junto do `origem` já traduzido porque
    // `origem` cai em "Avulso" tanto pro código 0 (SemDocumento) quanto por fallback de código
    // desconhecido (ver ORIGEM_POR_TIPO_DOCUMENTO) — pra decidir elegibilidade de Cancelar (regra do
    // backend é `TipoDocumento === SemDocumento`), precisa do código exato, não da tradução.
    tipoDocumento: number;
    // Preenchido só quando `origem === "Recorrencia"` — usado pra mostrar o id da regra no lugar do
    // `documento` (que pra lançamento gerado por recorrência traz a descrição da regra, não um
    // documento de verdade).
    lancamentoRecorrenciaId?: number | null;
    vencimento: Date;
    dataFluxo: Date;
    valorTitulo: number;
    saldo: number;
    vencido: boolean;
    // Renomeado de `observacao` (2026-09-19) — é o texto livre do cadastro/alteração (ou herdado da
    // regra de recorrência). `LancamentoVm.observacao` (motivo de cancelamento/prorrogação/abatimento)
    // não tem campo equivalente aqui: não é pra exibir na tela principal, só em histórico/apontamentos.
    descricao?: string | null;
    // Situação bancária (boleto) — máquina de estado SEPARADA da situação financeira (§4 do PDF). É o
    // `EBoletoStatus` real do módulo Boleto (o protótipo tinha estados de CNAB que o backend não tem).
    // Mesmo shape da API (`LancamentoVm.cobranca`), repassado sem conversão.
    cobranca?: BoletoCobrancaLancamentoVm | null;
    // Favorecido do lançamento (exatamente um dos 4 FKs de `LancamentoVm`) e espécie de cobrança — o boleto
    // pode ser gerado para lançamento a receber com cobrança em Boleto, de qualquer tipo de favorecido.
    tipoFavorecido?: ETipoFavorecidoLancamento;
    favorecidoId?: number | string;
    especieCobranca?: number;
}

// ⚠️ Nome mantido com "Titulo" pelo mesmo motivo de LancamentoTituloVm (colisão com dataAsLancamentoVm).
export function dataAsLancamentoTituloVm(data: any): LancamentoTituloVm {
    return data as LancamentoTituloVm;
}

const SITUACAO_FINANCEIRA_POR_CODIGO: Record<number, TSituacaoFinanceiraLancamento> = {
    1: "Previsto",
    2: "EmAberto",
    3: "Parcial",
    4: "Baixado",
    5: "Cancelado",
};

/** `TipoDocumento` (backend, `ETipoDocumentoLancamento`) não tem equivalente 1:1 com `TOrigemLancamento`
 * (não existe "Recorrência" nem distinção Nota Fiscal genérica x serviço no mesmo nível) — melhor
 * esforço, só pra manter a coluna/ícone de Origem da listagem preenchidos. Lançamento gerado por
 * recorrência usa o MESMO código `SemDocumento` do manual — por isso `origem`/`origemDescricao` são
 * calculados por `calcularOrigem()` abaixo, que checa `lancamentoRecorrenciaId` ANTES de cair aqui. */
const ORIGEM_POR_TIPO_DOCUMENTO: Record<number, TOrigemLancamento> = {
    0: "Avulso",          // SemDocumento
    1: "NFSaida",         // NotaFiscal
    3: "PedidoCompra",    // PedidoCompra
    4: "NFEntrada",       // NotaFiscalEntrada
    5: "CTe",              // Cte
    6: "NFServico",       // NotaFiscalServico
};

const calcularOrigem = (vm: LancamentoVm): { origem: TOrigemLancamento; origemDescricao: string } => {
    if (vm.lancamentoRecorrenciaId != null) {
        // Maiúsculo pra bater com o padrão das outras descrições, que vêm assim do backend
        // (`ETipoDocumentoLancamentoExtension.Descricao()`: "MANUAL", "NOTA FISCAL", ...).
        return {origem: "Recorrencia", origemDescricao: "RECORRÊNCIA"};
    }

    return {
        origem: ORIGEM_POR_TIPO_DOCUMENTO[vm.tipoDocumento] ?? "Avulso",
        origemDescricao: vm.tipoDocumentoDescricao,
    };
};

/** Papel do favorecido (Cliente/Fornecedor/Transportadora/Funcionário) — não existe como campo
 * explícito no backend (`LancamentoVm.cs`), só implicitamente: exatamente um dos 4 FKs vem
 * preenchido por lançamento. Mesma lógica já usada ad-hoc em `LancamentoCadastro.vue` pra
 * pré-selecionar o combo em alteração. */
const derivarPapelFavorecido = (vm: LancamentoVm): string | undefined => {
    if (vm.clienteId != null) return "Cliente";
    if (vm.fornecedorId != null) return "Fornecedor";
    if (vm.transportadoraId != null) return "Transportadora";
    if (vm.funcionarioId != null) return "Funcionário";
    return undefined;
};

const derivarFavorecido = (vm: LancamentoVm): {tipoFavorecido?: ETipoFavorecidoLancamento; favorecidoId?: number | string} => {
    if (vm.clienteId != null) return {tipoFavorecido: ETipoFavorecidoLancamento.Cliente, favorecidoId: vm.clienteId};
    if (vm.fornecedorId != null) return {tipoFavorecido: ETipoFavorecidoLancamento.Fornecedor, favorecidoId: vm.fornecedorId};
    if (vm.transportadoraId != null) return {tipoFavorecido: ETipoFavorecidoLancamento.Transportadora, favorecidoId: vm.transportadoraId};
    if (vm.funcionarioId != null) return {tipoFavorecido: ETipoFavorecidoLancamento.Funcionario, favorecidoId: vm.funcionarioId};
    return {};
}

/** Chave "tipo:id" do favorecido — o boleto agrupado só cobra lançamentos do mesmo favorecido. */
export function chaveFavorecidoLancamento(titulo: LancamentoTituloVm): string {
    return `${titulo.tipoFavorecido ?? 0}:${titulo.favorecidoId ?? 0}`;
}

const calcularVencido = (vencimento: Date, saldo: number, situacao: TSituacaoFinanceiraLancamento): boolean => {
    if (saldo <= 0 || situacao === "Baixado" || situacao === "Cancelado") return false;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataVencimento = new Date(vencimento);
    dataVencimento.setHours(0, 0, 0, 0);

    return dataVencimento.getTime() < hoje.getTime();
}

/** `LancamentoVm` (API) → `LancamentoTituloVm` (apresentação). */
export function lancamentoVmParaTitulo(vm: LancamentoVm): LancamentoTituloVm {
    const situacaoFinanceira = SITUACAO_FINANCEIRA_POR_CODIGO[vm.situacaoFinanceira ?? 2] ?? "EmAberto";
    const saldo = vm.saldoAberto ?? 0;
    // "Data vencimento no sigest porem seguimos o do banco" (comentário do próprio backend) — no
    // banco, o vencimento é DataLancamento e o fluxo é DataFaturamento.
    const vencimento = vm.dataLancamento;
    const {origem, origemDescricao} = calcularOrigem(vm);

    return {
        id: vm.id,
        tipo: vm.tipoLancamento as ETipoLancamento,
        tipoDescricao: vm.tipoLancamentoDescricao,
        situacaoFinanceira,
        situacaoFinanceiraDescricao: vm.situacaoFinanceiraDescricao ?? situacaoFinanceira,
        favorecido: vm.favorecido,
        papelFavorecido: derivarPapelFavorecido(vm),
        documento: vm.documento,
        parcela: vm.parcela,
        origem,
        origemDescricao,
        tipoDocumento: vm.tipoDocumento,
        lancamentoRecorrenciaId: vm.lancamentoRecorrenciaId,
        vencimento,
        dataFluxo: vm.dataFaturamento,
        valorTitulo: vm.valorLancamento,
        saldo,
        vencido: calcularVencido(vencimento, saldo, situacaoFinanceira),
        descricao: vm.descricao,
        cobranca: vm.cobranca ?? null,
        ...derivarFavorecido(vm),
        especieCobranca: vm.especieCobranca,
    };
}

/** Boleto ativo prende o lançamento: o backend bloqueia alterar/cancelar/prorrogar/abater/remover até o
 * boleto ser cancelado, baixado ou liquidado. A baixa manual continua permitida (pagamento por fora). */
export function lancamentoTemBoletoAtivo(titulo: LancamentoTituloVm): boolean {
    return !!titulo.cobranca?.ativo;
}

/** Mesma regra de `BoletoLancamentoService.AvaliarElegibilidade` (backend), replicada só para mostrar a
 * ação na tela: a Receber, com cobrança em Boleto, de qualquer tipo de favorecido, Em aberto ou Parcial,
 * com saldo e sem boleto ativo. O backend valida de novo no `preparar`/`gerar` (inclusive "mesmo
 * favorecido" no agrupamento). */
export function lancamentoPodeGerarBoleto(titulo: LancamentoTituloVm): boolean {
    return titulo.tipo === ETipoLancamento.AReceber
        && titulo.especieCobranca === ESPECIE_COBRANCA_BOLETO
        && titulo.tipoFavorecido != null
        && Number(titulo.favorecidoId) > 0
        && (titulo.situacaoFinanceira === "EmAberto" || titulo.situacaoFinanceira === "Parcial")
        && titulo.saldo > 0
        && !lancamentoTemBoletoAtivo(titulo);
}

/** Uma baixa (liquidação) do título — um título pode ter várias (baixa parcial). Shape de
 * APRESENTAÇÃO (nomes curtos, iguais ao protótipo); a API devolve `LancamentoBaixaApiVm` (nomes
 * `valorXxx`, mais abaixo) — a conversão é `lancamentoBaixaApiParaVm()`. */
export interface LancamentoBaixaVm {
    id: number;
    dataBaixa: Date;
    principal: number;
    desconto: number;
    juros: number;
    multa: number;
    correcaoMonetaria: number;
    retencaoIr: number;
    taxaBanco: number;
    total: number;
    especieBaixaDescricao: string;
    contaDescricao: string;
    comprovanteAnexoId?: number | null;
}

/** Anexo do lançamento: boleto a pagar (tipo 1) ou comprovante de pagamento (tipo 2). Shape de
 * APRESENTAÇÃO; a API devolve `LancamentoAnexoApiVm` (`tipoAnexo`/`tipoAnexoDescricao`, mais abaixo)
 * — conversão em `lancamentoAnexoApiParaVm()`. */
export interface LancamentoAnexoVm {
    id: number;
    tipo: number;
    tipoDescricao: string;
    nomeArquivo: string;
}

/** Resposta de `lancamento/detalhe/consultar`: o título + baixas + anexos. NÃO inclui histórico de
 * eventos (prorrogado/abatido/...) — isso vem só de `lancamento/historico/consultar`, endpoint
 * separado (chamar em paralelo se algum dia precisar). `LancamentoDetalhe.vue` não tem mais painel de
 * histórico (removido a pedido do usuário), por isso não há campo `historico` aqui. */
export interface LancamentoDetalheVm extends LancamentoTituloVm {
    // Só aparecem no detalhe (o protótipo não mostra isso na listagem).
    // ⚠️ Sem fonte real — backend não tem natureza financeira ainda (ver
    // for_claude/DECISOES_ABERTAS.md item 5); mapper nunca preenche, template já trata como opcional.
    naturezaFinanceiraDescricao?: string;
    dataEmissao?: Date | null;
    // Espécie/conta informadas na criação do lançamento (`LancamentoVm.especieCobranca`/`conta`) — usadas
    // só pra pré-preencher o formulário de "Nova baixa" em `LancamentoDetalhe.vue` (2026-09-18, pedido
    // do usuário), o usuário ainda pode trocar antes de gravar.
    especieLancamento?: {codigo: number; descricao: string};
    conta?: ContaVm;
    baixas: LancamentoBaixaVm[];
    anexos: LancamentoAnexoVm[];
}

/**
 * A PARTIR DAQUI: shapes EXATOS devolvidos pela API (`lancamento/detalhe/consultar`,
 * `lancamento/baixa/*`, `lancamento/anexo/*`, `lancamento/historico/consultar`) — lidos direto do
 * backend (`LancamentoCtx/ViewModels/`) em 2026-09-15. Nome com sufixo `Api` pra não colidir com o
 * shape de apresentação acima. Só quem deveria importar isto é `lancamento.service.ts`; componentes
 * trabalham com os Vm de apresentação (conversores logo após cada interface).
 */
export interface LancamentoBaixaApiVm {
    id: number;
    lancamentoId: number;
    dataBaixa: Date;
    dataBaixaFormatada: string;
    valorPrincipal: number;
    valorDesconto: number;
    valorJuros: number;
    valorMulta: number;
    valorCorrecaoMonetaria: number;
    valorRetencaoIr: number;
    valorTaxaBanco: number;
    valorTotal: number;
    especieBaixa: number;
    especieBaixaDescricao: string;
    contaBaixa?: ContaVm | null;
    anexoComprovanteId?: number | null;
    anexoComprovanteNomeArquivo?: string | null;
}

export function lancamentoBaixaApiParaVm(baixa: LancamentoBaixaApiVm): LancamentoBaixaVm {
    return {
        id: baixa.id,
        dataBaixa: baixa.dataBaixa,
        principal: baixa.valorPrincipal,
        desconto: baixa.valorDesconto,
        juros: baixa.valorJuros,
        multa: baixa.valorMulta,
        correcaoMonetaria: baixa.valorCorrecaoMonetaria,
        retencaoIr: baixa.valorRetencaoIr,
        taxaBanco: baixa.valorTaxaBanco,
        total: baixa.valorTotal,
        especieBaixaDescricao: baixa.especieBaixaDescricao,
        contaDescricao: baixa.contaBaixa?.descricaoConta ?? "--",
        comprovanteAnexoId: baixa.anexoComprovanteId ?? null,
    };
}

export interface LancamentoAnexoApiVm {
    id: number;
    lancamentoId: number;
    tipoAnexo: number;
    tipoAnexoDescricao: string;
    nomeArquivo: string;
    tipoConteudoArquivo: string;
}

export function lancamentoAnexoApiParaVm(anexo: LancamentoAnexoApiVm): LancamentoAnexoVm {
    return {
        id: anexo.id,
        tipo: anexo.tipoAnexo,
        tipoDescricao: anexo.tipoAnexoDescricao,
        nomeArquivo: anexo.nomeArquivo,
    };
}

export interface LancamentoDetalheApiVm {
    lancamento: LancamentoVm;
    baixas: LancamentoBaixaApiVm[];
    anexos: LancamentoAnexoApiVm[];
}

export function lancamentoDetalheApiParaVm(detalhe: LancamentoDetalheApiVm): LancamentoDetalheVm {
    return {
        ...lancamentoVmParaTitulo(detalhe.lancamento),
        dataEmissao: detalhe.lancamento.dataDocumentoEmissao ?? null,
        especieLancamento: detalhe.lancamento.especieCobranca
            ? {codigo: detalhe.lancamento.especieCobranca, descricao: detalhe.lancamento.especieCobrancaDescricao}
            : undefined,
        conta: detalhe.lancamento.conta ?? undefined,
        baixas: detalhe.baixas.map(lancamentoBaixaApiParaVm),
        anexos: detalhe.anexos.map(lancamentoAnexoApiParaVm),
    };
}

/** Evento de histórico (`TB_LA_LOG`, via `lancamento/historico/consultar`) — espelha
 * `LancamentoLogVm.cs` (backend), confirmado em 2026-09-18 (a versão anterior deste tipo tinha nomes
 * de campo que nunca existiram de verdade na resposta — `data`/`dataFormatada`/`descricao`/`usuario`/
 * `status`/`statusDescricao` —, o que fazia o `sortField="data"` de `LancamentoLog.vue` comparar
 * `undefined` em toda linha e nunca ordenar de verdade). */
export interface LancamentoHistoricoApiVm {
    lancamentoLogId: number;
    lancamentoId: number;
    dataHistorico: Date;
    dataHistoricoFormatada: string;
    descricaoHistorico: string;
    nomeFuncionario: string;
    statusLancamento: number;
    statusLancamentoDescricao: string;
}

/** A baixa manual continua liberada com boleto ativo; quando quita o saldo, o backend devolve o aviso para
 * solicitar a remessa de baixa na própria `message` do sucesso ("ATENÇÃO: BOLETO ... AINDA ATIVO NO BANCO" na
 * baixa individual, "... com boleto ainda ativo no banco ..." na baixa em lote). */
export function mensagemBaixaTemAvisoBoleto(mensagem?: string | null): boolean {
    return !!mensagem && /boleto.*ativo no banco/i.test(mensagem);
}
